/**
 * Accès à la gestion des grilles d'évaluation personnalisées (« Mes grilles »).
 *
 * Règle métier : la **création** et l'**import** de grilles sont réservés aux parcs
 * de niveau **Essentiel et supérieur**. Un parc Discovery (prospect, niveau
 * découverte) peut utiliser les grilles qui lui sont diffusées, mais pas en créer.
 *
 * La règle s'applique à tout le monde, **membres AKONGO compris** : un membre qui
 * parcourt un parc Discovery ne voit pas l'entrée de menu « Mes grilles » et les
 * vues de création / import sont fermées. Pour gérer des grilles, il bascule sur un
 * parc Essentiel+ ou sur le zoo akongo (dont le tier « akongo » est un accès plein,
 * hors `TIER_HIERARCHY`).
 *
 * Tier consommé : `followUpLevel` = `zoo/{zooId}.zooTiers.value`, résolu par
 * `UserDatabaseConnector._getZooData` (legacy `basic` → `essential`), exposé côté
 * Redux sous `state.zoo.zooTier` et passé à `generateMenu(zooTier, …)`. Le menu et
 * les vues appellent donc ce helper : ils ne peuvent pas diverger.
 *
 * ⚠️ Gate **UI uniquement** : `firestore.rules` continue d'autoriser l'écriture sur
 * `gridTemplates` à un membre AKONGO ou au propriétaire du zoo (règle inchangée).
 * Ce module ferme le menu et les vues, pas la base.
 */

import { hasTierAccess } from "../config/tierConfig";

/** Tier minimum pour créer / importer ses propres grilles. */
export const GRID_MANAGEMENT_MIN_TIER = "essential";

/**
 * Normalise un tier reçu sous l'une de ses formes runtime.
 *
 * @param {string|{value?: string}|null|undefined} zooTier tier brut (chaîne,
 *   objet react-select `{value, label}`, `zooTiers` d'un document Firestore)
 * @returns {string|null} tier normalisé, ou `null` si illisible
 */
export function normalizeZooTier(zooTier) {
  const raw = zooTier && typeof zooTier === "object" ? zooTier.value : zooTier;
  if (typeof raw !== "string") return null;
  const tier = raw.trim();
  if (!tier) return null;
  // Valeur legacy encore portée par des parcs anciens (cf. _getZooData).
  return tier === "basic" ? "essential" : tier;
}

/**
 * Le tier courant autorise-t-il la gestion des grilles (création / import) ?
 *
 * Fail-closed : un tier absent ou inconnu ferme l'accès. Seule exception, le tier
 * plein du zoo AKONGO (« akongo »), qui n'appartient pas à `TIER_HIERARCHY`.
 *
 * @param {string|{value?: string}|null|undefined} zooTier
 * @returns {boolean}
 */
export function isGridManagementTierAllowed(zooTier) {
  const tier = normalizeZooTier(zooTier);
  if (!tier) return false;
  if (tier === "akongo") return true;
  return hasTierAccess(tier, GRID_MANAGEMENT_MIN_TIER);
}
